import { meQueryOptions } from "@/api/auth";
import { myLocationQueryOptions } from "@/api/me";
import { supplierListingsQueryOptions, supplierQueryOptions } from "@/api/suppliers";
import { queryClient } from "@/api/utils";
import { ListingCard } from "@/components/ListingCard";
import { Markdown } from "@/components/Markdown";
import { MessageSupplierForm } from "@/components/MessageSupplierForm";
import { BackIconButton } from "@/components/ui/BackButton";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { Box, Grid, Heading, Img, QueryHandler, Text } from "@givenwell/components";
import { colors, fonts } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, useCanGoBack, useNavigate, useRouter } from "@tanstack/react-router";

export const Route = createFileRoute("/_auth/_app/marketplace/suppliers/$supplierId")({
  component: Page,
  loader: async ({ params }) => {
    const me = await queryClient.ensureQueryData(meQueryOptions);
    const location = await queryClient.ensureQueryData(myLocationQueryOptions);
    const countryCode = location?.country_code ?? null;
    queryClient.prefetchQuery(supplierListingsQueryOptions(params.supplierId, countryCode));
    return { countryCode };
  },
});

const route = getRouteApi("/_auth/_app/marketplace/suppliers/$supplierId");

function Page() {
  const { supplierId } = route.useParams();
  const { countryCode } = route.useLoaderData();
  const supplierQuery = useQuery(supplierQueryOptions(supplierId));
  const listingsQuery = useQuery(supplierListingsQueryOptions(supplierId, countryCode));
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  return (
    <PageWrapper css={{ pb: 0 }}>
      <QueryHandler
        query={supplierQuery}
        success={({ data: supplier }) => (
          <>
            <Box css={{ width: "100%", maxW: 960, mx: "auto" }}>
              <Box
                css={{
                  pos: "relative",
                  width: "100%",
                  aspectRatio: 21 / 9,
                  // maxH: 180,
                  bg: colors.warmGrey,
                  "@lg": {
                    aspectRatio: 32 / 9,
                    borderRadius: 12,
                    overflow: "hidden",
                  },
                }}
              >
                {supplier.cover_image_media && (
                  <Img
                    src={supplier.cover_image_media.url}
                    css={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}
                <BackIconButton
                  onClick={() => {
                    if (canGoBack) {
                      router.history.back();
                    } else {
                      navigate({
                        to: "/marketplace",
                      });
                    }
                  }}
                />
              </Box>
              <Box
                css={{
                  pl: "max(16px, var(--safe-area-inset-left))",
                  "@lg": { display: "flex", justify: "center" },
                }}
              >
                <Box
                  css={{
                    bg: colors.warmGrey,
                    position: "relative",
                    top: -96 / 2,
                    width: 96,
                    height: 96,
                    aspectRatio: 1,
                    objectFit: "cover",
                    borderRadius: "50%",
                    border: `4px solid ${colors.cream}`,
                    overflow: "hidden",

                    "@lg": {
                      top: -120 / 2,
                      width: 120,
                      height: 120,
                    },
                  }}
                >
                  {supplier.profile_image_media && (
                    <Img
                      src={supplier.profile_image_media.url}
                      css={{
                        w: "100%",
                        h: "100%",
                      }}
                    />
                  )}
                </Box>
              </Box>
              <Box css={{ mt: -96 / 2 - 4 }} />
              <Box
                css={{
                  p: 16,
                  pt: 24,
                  pl: "max(16px, var(--safe-area-inset-left))",
                  pr: "max(16px, var(--safe-area-inset-right))",
                }}
              >
                <Heading
                  as="h1"
                  css={{
                    fontScale: "xl",
                    weight: 500,
                    fontFamily: fonts.ftHabit,
                    "@lg": {
                      fontScale: "2xl",
                    },
                  }}
                >
                  {supplier.name}
                </Heading>
                {supplier.bio && (
                  <Text css={{ color: colors.text2, mt: 16 }}>
                    <Markdown markdown={supplier.bio} />
                  </Text>
                )}
              </Box>

              <Box
                css={{
                  pl: "max(16px, var(--safe-area-inset-left))",
                  pr: "max(16px, var(--safe-area-inset-right))",
                  pb: "max(16px, var(--safe-area-inset-bottom))",
                }}
              >
                <Heading
                  as="h2"
                  css={{
                    weight: 500,
                    fontScale: "md",
                    pb: 10,
                    fontFamily: fonts.ftHabit,
                    "@lg": {
                      align: "center",
                      py: 40,
                      fontScale: "xl",
                    },
                  }}
                >
                  Listings
                </Heading>
                <QueryHandler
                  query={listingsQuery}
                  success={({ data: listings }) => (
                    <Grid
                      css={{
                        gap: 16,
                        gridCols: 2,
                        rowGap: 32,
                        "@lg": { gridCols: 4 },
                      }}
                    >
                      {listings.map(listing => (
                        <ListingCard key={listing.listing_id} listing={listing} />
                      ))}
                    </Grid>
                  )}
                />
              </Box>
            </Box>
            <MessageSupplierForm supplierId={supplier.id} supplierName={supplier.name} />
            <Box css={{ mb: 192 }} />
          </>
        )}
      />
    </PageWrapper>
  );
}
